import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Ticket, Sparkles, Copy, Search, Gift, CheckCircle2, Clock, XCircle } from "lucide-react";

const STATUS = {
  active: { l: "نشط", c: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  used: { l: "مستخدم", c: "bg-slate-100 text-slate-500", Icon: XCircle },
  expired: { l: "منتهي", c: "bg-red-100 text-red-700", Icon: Clock },
};

export default function LoyaltyVouchers() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dlg, setDlg] = useState(false);
  const [customerQ, setCustomerQ] = useState("");

  const load = async () => {
    try {
      const [v, c] = await Promise.all([
        api.get(`/loyalty/vouchers${statusFilter ? `?status=${statusFilter}` : ""}`),
        api.get("/customers"),
      ]);
      setItems(v.data); setCustomers(c.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const convert = async (customerId, customerName) => {
    if (!window.confirm(`تحويل 500 نقطة من رصيد ${customerName} إلى كوبون خصم 10%؟`)) return;
    try {
      const { data } = await api.post(`/loyalty/vouchers/convert/${customerId}`);
      toast.success(`تم إنشاء الكوبون: ${data.code}`);
      setDlg(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const copy = (text) => { try { navigator.clipboard.writeText(text); toast.success("تم النسخ"); } catch {} };

  const filteredItems = items.filter(v => !filter || v.customer_name?.toLowerCase().includes(filter.toLowerCase()) || v.code?.toLowerCase().includes(filter.toLowerCase()));

  const eligibleCustomers = customers.filter(c => (c.loyalty_points || 0) >= 500 && (!customerQ || c.name?.toLowerCase().includes(customerQ.toLowerCase())));

  const stats = {
    active: items.filter(v => v.status === "active").length,
    used: items.filter(v => v.status === "used").length,
    expired: items.filter(v => v.status === "expired").length,
  };

  return (
    <div className="space-y-4" data-testid="vouchers-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><Ticket className="w-6 h-6 text-primary" /> كوبونات الولاء</h1>
          <p className="text-sm text-muted-foreground mt-1">حوّل 500 نقطة إلى كوبون خصم 10% صالح 60 يوماً</p>
        </div>
        <Button onClick={() => setDlg(true)} className="gap-2" data-testid="new-voucher-btn"><Sparkles className="w-4 h-4" /> تحويل نقاط إلى كوبون</Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {Object.entries(STATUS).map(([k, s]) => {
          const SIcon = s.Icon;
          return (
            <Card key={k}><CardContent className="p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full grid place-items-center ${s.c}`}><SIcon className="w-5 h-5" /></div>
              <div><div className="text-xs text-muted-foreground">{s.l}</div><div className="text-2xl font-bold">{stats[k] || 0}</div></div>
            </CardContent></Card>
          );
        })}
      </div>

      <div className="flex gap-2 items-center flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={filter} onChange={e => setFilter(e.target.value)} placeholder="بحث بالكود أو الاسم" className="pe-9" data-testid="v-search" />
        </div>
        <div className="flex gap-1 border rounded p-1 bg-muted/30 text-xs">
          {["", "active", "used", "expired"].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} className={`px-2 py-1 rounded transition ${statusFilter === s ? "bg-primary text-primary-foreground" : "hover:bg-background"}`} data-testid={`filter-${s || "all"}`}>
              {s === "" ? "الكل" : STATUS[s].l}
            </button>
          ))}
        </div>
      </div>

      {filteredItems.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed rounded-lg text-muted-foreground">
          <Ticket className="w-16 h-16 mx-auto opacity-30 mb-3" />
          <div>لا توجد كوبونات</div>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b"><tr>
              <th className="text-start p-3">الكود</th>
              <th className="text-start p-3">العميل</th>
              <th className="text-center p-3">الخصم</th>
              <th className="text-center p-3">الحالة</th>
              <th className="text-start p-3">صالح حتى</th>
              <th className="text-start p-3">فاتورة الاستخدام</th>
              <th className="text-center p-3">إجراء</th>
            </tr></thead>
            <tbody>{filteredItems.map(v => {
              const s = STATUS[v.status] || STATUS.active;
              return (
                <tr key={v.id} className="border-b hover:bg-muted/30" data-testid={`voucher-${v.id}`}>
                  <td className="p-3 font-mono font-bold text-primary">{v.code}</td>
                  <td className="p-3">{v.customer_name || "—"}</td>
                  <td className="p-3 text-center font-bold text-emerald-600">{v.discount_percent}%</td>
                  <td className="p-3 text-center"><span className={`px-2 py-0.5 rounded text-xs ${s.c}`}>{s.l}</span></td>
                  <td className="p-3 font-mono text-xs">{new Date(v.expires_at).toLocaleDateString("ar-EG")}</td>
                  <td className="p-3 text-xs">{v.used_invoice_number || "—"}</td>
                  <td className="p-3 text-center">
                    {v.status === "active" && <Button size="icon" variant="ghost" onClick={() => copy(v.code)} className="h-7 w-7" title="نسخ الكود"><Copy className="w-3.5 h-3.5" /></Button>}
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-lg" data-testid="convert-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Gift className="w-5 h-5 text-primary" /> تحويل نقاط إلى كوبون</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded bg-primary/5 border border-primary/20 text-sm">
              💡 كل <b>500 نقطة</b> = كوبون خصم <b>10%</b> صالح <b>60 يوماً</b>
            </div>
            <div>
              <Label>ابحث عن عميل مؤهل (≥ 500 نقطة)</Label>
              <Input value={customerQ} onChange={e => setCustomerQ(e.target.value)} placeholder="اسم العميل..." data-testid="customer-search" />
            </div>
            <div className="border rounded max-h-80 overflow-y-auto">
              {eligibleCustomers.length === 0 ? (
                <div className="text-center py-8 text-xs text-muted-foreground">
                  لا يوجد عملاء مؤهلون — يحتاج العميل 500 نقطة على الأقل
                </div>
              ) : eligibleCustomers.map(c => (
                <div key={c.id} className="p-2 border-b last:border-0 flex items-center justify-between hover:bg-muted/30" data-testid={`elig-${c.id}`}>
                  <div>
                    <div className="text-sm font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground">{c.phone || ""}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-primary font-bold text-sm">{Math.floor(c.loyalty_points || 0)} نقطة</div>
                    <Button size="sm" onClick={() => convert(c.id, c.name)} data-testid={`convert-${c.id}`}>تحويل</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlg(false)}>إغلاق</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
